import { useTranslation } from 'react-i18next';

/**
 * Translation utility service for inline translations without JSON files
 * 
 * Usage:
 * 1. Simple string pair:
 *    const text = useInlineTranslation("Hello", "Bonjour");
 * 
 * 2. Object format:
 *    const text = useInlineTranslation(
 *      { lang: "en", text: "Hello" },
 *      { lang: "fr", text: "Bonjour" }
 *    );
 */

// Type definitions
type TranslationString = string;
type TranslationObject = { lang: 'en' | 'fr'; text: string };
type TranslationInput = TranslationString | TranslationObject;

/**
 * Hook version - for use in React components
 */
export function useInlineTranslation(
  english: TranslationInput,
  french: TranslationInput
): string {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  // Extract text from input (handles both string and object formats)
  const getTextFromInput = (input: TranslationInput): string => {
    return typeof input === 'string' ? input : input.text;
  };

  // Return appropriate translation based on current language
  if (currentLang === 'fr' || currentLang.startsWith('fr')) {
    return getTextFromInput(french);
  }
  
  return getTextFromInput(english);
}

/**
 * Direct function version - for use outside React components
 * Pass the current language explicitly
 */
export function getInlineTranslation(
  currentLang: string,
  english: TranslationInput,
  french: TranslationInput
): string {
  // Extract text from input (handles both string and object formats)
  const getTextFromInput = (input: TranslationInput): string => {
    return typeof input === 'string' ? input : input.text;
  };

  // Return appropriate translation based on language parameter
  if (currentLang === 'fr' || currentLang.startsWith('fr')) {
    return getTextFromInput(french);
  }
  
  return getTextFromInput(english);
}

/**
 * Batch translation helper - translate multiple strings at once
 * Returns an object with the same keys but translated values
 */
export function useInlineTranslations<T extends Record<string, [TranslationInput, TranslationInput]>>(
  translations: T
): { [K in keyof T]: string } {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const result: any = {};
  
  for (const key in translations) {
    const [english, french] = translations[key];
    result[key] = getInlineTranslation(currentLang, english, french);
  }

  return result;
}

/**
 * Helper to create translation objects more easily
 */
export const createTranslation = (en: string, fr: string) => ({
  en: { lang: 'en' as const, text: en },
  fr: { lang: 'fr' as const, text: fr }
});

// Export types for use in other files
export type { TranslationInput, TranslationObject };
